import React, { useEffect, useMemo, useState } from 'react';

import {
	Chart as ChartJS,
	LineElement,
	PointElement,
	LinearScale,
	TimeScale,
	Tooltip,
	Legend,
	Filler,
	CategoryScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { useAuditTimeseries } from '@/hook/useAuditTimeseries';
import './audit-timeseries.css';

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend, Filler, CategoryScale);

function toTs(x) {
	if (x == null) return NaN;
	if (typeof x === 'number') return x < 2000000000 ? x * 1000 : x; // sec -> ms
	const n = Number(x);
	if (!Number.isNaN(n)) return toTs(n);
	const d = new Date(x).getTime();
	return Number.isNaN(d) ? NaN : d;
}

function isFinitePoint(p) {
	return Number.isFinite(p.ts) && Number.isFinite(p.value);
}

function normalize(raw) {
	// Expecting: { interval, metric, series: [{ t, value }] }
	if (!raw || raw.error) return [];
	const arr = Array.isArray(raw.series) ? raw.series : [];
	return arr
		.map(p => ({ ts: toTs(p.t), value: Number(p.value) }))
		.filter(isFinitePoint)
		.sort((a, b) => a.ts - b.ts);
}

function isoLocal(date) {
	const d = new Date(date);
	d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
	return d.toISOString().slice(0, 16);
}

function defaultToISO() {
	return isoLocal(new Date());
}

function defaultFromISO(hoursBack = 24) {
	const d = new Date();
	d.setHours(d.getHours() - hoursBack);
	return isoLocal(d);
}

function safeStringify(obj, spaces = 0) {
	try {
		return JSON.stringify(obj, null, spaces);
	} catch {
		return String(obj);
	}
}

function clampToNow(isoStr) {
	const ms = toTs(isoStr);
	const now = Date.now();
	if (!Number.isFinite(ms)) return isoStr;
	return isoLocal(Math.min(ms, now));
}

function fmtInt(n) {
	try {
		return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
	} catch {
		return String(n);
	}
}

function useDebouncedValue(value, delayMs = 300) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(id);
	}, [value, delayMs]);
	return debounced;
}

export default function AuditTimeseriesPanel() {
	const [from, setFrom] = useState(() => defaultFromISO(24));
	const [to, setTo] = useState(() => defaultToISO());
	const [interval, setInterval] = useState('minute');
	const [metric, setMetric] = useState('count');
	const [fieldType, setFieldType] = useState('');

	// keep "to" from drifting into the future if the page is left open
	useEffect(() => {
		const id = setInterval(() => {
			setTo(prev => clampToNow(prev));
		}, 60_000);
		return () => clearInterval(id);
	}, []);

	// validate and normalize date range
	const fromMs = toTs(from);
	const toMs = toTs(to);
	const datesValid = Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs <= toMs;

	// debounce params to avoid hammering the data hook while typing
	const debouncedParams = useDebouncedValue(
		{
			from: datesValid ? from : undefined,
			to: datesValid ? to : undefined,
			interval,
			metric,
			fieldType: fieldType || undefined
		},
		350
	);

	const dataRaw = useAuditTimeseries(debouncedParams);
	const points = useMemo(() => normalize(dataRaw), [dataRaw]);

	const isLoading = dataRaw == null;
	const isEmpty = !isLoading && points.length === 0 && !dataRaw?.error;

	// Prefer server-provided labels if present
	const effectiveInterval = dataRaw?.interval || interval;
	const effectiveMetric = dataRaw?.metric || metric;

	const chartData = useMemo(
		() => ({
			labels: points.map(p => new Date(p.ts)),
			datasets: [
				{
					label: `${effectiveMetric} per ${effectiveInterval}`,
					data: points.map(p => p.value),
					fill: true,
					tension: 0.25,
					pointRadius: 2,
					borderWidth: 2
				}
			]
		}),
		[points, effectiveInterval, effectiveMetric]
	);

	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: true },
				tooltip: {
					enabled: true,
					callbacks: {
						label: ctx => `${ctx.dataset.label}: ${fmtInt(ctx.parsed.y)}`
					}
				}
			},
			scales: {
				x: {
					type: 'time',
					time: { tooltipFormat: 'MMM d, yyyy HH:mm' },
					ticks: { maxRotation: 0, autoSkip: true }
				},
				y: {
					beginAtZero: true,
					ticks: {
						precision: 0,
						callback: v => fmtInt(v)
					},
					title: { display: true, text: String(effectiveMetric).toUpperCase() }
				}
			}
		}),
		[effectiveMetric]
	);

	function applyQuickRange(hoursBack) {
		setFrom(defaultFromISO(hoursBack));
		setTo(defaultToISO());
	}

	function downloadCSV() {
		const rows = [
			['timestamp', 'iso', String(effectiveMetric)],
			...points.map(p => [String(Math.floor(p.ts / 1000)), new Date(p.ts).toISOString(), String(p.value)])
		];
		const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `audit_timeseries_${effectiveMetric}_${effectiveInterval}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}

	return (
		<div className="audit-card" role="region" aria-label="Audit Timeseries">
			<div className="audit-header">
				<h2>Audit Timeseries</h2>

				<div className="audit-controls">
					<div className="control">
						<label htmlFor="from">From</label>
						<input id="from" type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} />
					</div>

					<div className="control">
						<label htmlFor="to">To</label>
						<input id="to" type="datetime-local" value={to} onChange={e => setTo(clampToNow(e.target.value))} />
					</div>

					<div className="control">
						<label htmlFor="interval">Interval</label>
						<select id="interval" value={interval} onChange={e => setInterval(e.target.value)}>
							<option value="minute">Minute</option>
							<option value="hour">Hour</option>
							<option value="day">Day</option>
						</select>
					</div>

					<div className="control">
						<label htmlFor="metric">Metric</label>
						<select id="metric" value={metric} onChange={e => setMetric(e.target.value)}>
							<option value="count">Count</option>
							<option value="p95">p95</option>
						</select>
					</div>

					<div className="control">
						<label htmlFor="fieldType">Field Type</label>
						<input
							id="fieldType"
							type="text"
							placeholder="(optional)"
							value={fieldType}
							onChange={e => setFieldType(e.target.value)}
						/>
					</div>

					<div className="control control--row">
						<span className="sr-only" aria-hidden="false">
							Quick ranges
						</span>
						<button type="button" onClick={() => applyQuickRange(24)} title="Last 24 hours">
							24h
						</button>
						<button type="button" onClick={() => applyQuickRange(24 * 7)} title="Last 7 days">
							7d
						</button>
						<button type="button" onClick={() => applyQuickRange(24 * 30)} title="Last 30 days">
							30d
						</button>
					</div>

					<div className="control">
						<button type="button" onClick={downloadCSV} disabled={points.length === 0}>
							Export CSV
						</button>
					</div>
				</div>
			</div>

			<div className="audit-body">
				{!datesValid && <div className="state">Select a valid date range (From must be ≤ To).</div>}

				{datesValid && isLoading && <div className="state muted">Loading…</div>}

				{datesValid && dataRaw?.error && (
					<div className="state">Failed to load{dataRaw?.message ? `: ${dataRaw.message}` : '.'}</div>
				)}

				{datesValid && isEmpty && <div className="state">No data for the selected filters.</div>}

				{datesValid && !isLoading && !isEmpty && !dataRaw?.error && (
					<>
						<div className="chart-meta">
							<span>Points: {fmtInt(points.length)}</span>
							{dataRaw?.interval && <span>Interval: {dataRaw.interval}</span>}
							{dataRaw?.metric && <span>Metric: {dataRaw.metric}</span>}
						</div>
						<div className="chart-wrap" style={{ height: 320 }}>
							<Line data={chartData} options={chartOptions} />
						</div>
					</>
				)}
			</div>

			<details className="debug">
				<summary>Debug / Raw JSON</summary>
				<pre>{safeStringify(dataRaw, 2)}</pre>
			</details>
		</div>
	);
}
